import { Account, DeselectAll, Inventory, Reset } from '@app/components/various/Icons';
import { AccountRequestStatus } from '@app/constants/requests';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  accountRequestStatus: AccountRequestStatus;
  openCreateModal: VoidFunction;
  openDeclineModal: VoidFunction;
  openArchiveModal: VoidFunction;
  onSetToPending: VoidFunction;
}

export const useAccountRequestListOptions = ({
  accountRequestStatus,
  openCreateModal,
  openDeclineModal,
  openArchiveModal,
  onSetToPending,
}: Props) => {
  const { t } = useTranslation(['accounts', 'requests']);

  const createAction = React.useMemo(
    () => ({
      icon: Account,
      key: 'createAccount',
      label: t('accounts:create_account'),
      onClick: openCreateModal,
    }),
    [openCreateModal, t],
  );

  const declineAction = React.useMemo(
    () => ({
      icon: DeselectAll,
      key: 'declineRequest',
      label: t('accounts:decline_request'),
      onClick: openDeclineModal,
    }),
    [openDeclineModal, t],
  );

  const archiveAction = React.useMemo(
    () => ({
      icon: Inventory,
      key: 'archiveRequest',
      label: t('accounts:archive_request'),
      onClick: openArchiveModal,
    }),
    [openArchiveModal, t],
  );

  const reactivateAction = React.useMemo(
    () => ({
      icon: Reset,
      key: 'reactivateRequest',
      label: t('requests:reactivate'),
      onClick: onSetToPending,
    }),
    [onSetToPending, t],
  );

  const getActionsBasedOnStatus = React.useMemo(
    () => (status: AccountRequestStatus) => {
      switch (status) {
        case AccountRequestStatus.Pending:
          return [createAction, declineAction, archiveAction];
        case AccountRequestStatus.Accepted:
          return [archiveAction];
        case AccountRequestStatus.Archived:
          return [];
        case AccountRequestStatus.Declined:
          return [createAction, archiveAction, reactivateAction];
        default:
          return [];
      }
    },
    [archiveAction, createAction, declineAction, reactivateAction],
  );

  return React.useMemo(() => {
    return getActionsBasedOnStatus(accountRequestStatus);
  }, [accountRequestStatus, getActionsBasedOnStatus]);
};
